import { FormApi } from "final-form";
import React from "react";
import { useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";

import { OtaFile } from "@dashboard/rollouts/types";
import { getOtaFileReleaseNotes, hasOtaFileReleaseNotes } from "@dashboard/rollouts/utils/otaFileReleaseNotes";
import { Box, Typography } from "@mui/material";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { Tooltip } from "@shared/Tooltip";

import { countSelectedDevices, DeviceFormKey, RolloutFormValues } from "../CreateRolloutSidePanel.utils";
import { RolloutUnifiedTree } from "../RolloutUnifiedTree/RolloutUnifiedTree";

import { useStyles } from "./DeviceStep.styles";
import { useUnifiedTree } from "./DeviceStep.utils";
import { ReleaseNotesCard } from "./ReleaseNotesCard";

const batchChangeThreshold = 300;

type Props = {
    onNext: () => void;
    onCancel: () => void;
    otaFile: OtaFile;
};

export const DeviceStep = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels", "common"]);

    const form: FormApi<RolloutFormValues> = useForm();
    const { values } = useFormState<RolloutFormValues>();

    const unifiedTree = useUnifiedTree(props.otaFile);
    const releaseNotes = getOtaFileReleaseNotes(props.otaFile);
    const hasReleaseNotes = hasOtaFileReleaseNotes(releaseNotes);

    const selectedDeviceCount = countSelectedDevices(values);
    const isNextDisabled = selectedDeviceCount === 0;

    const handleBatchChange = React.useCallback(
        (updates: Array<{ id: DeviceFormKey; value: boolean }>) => {
            if (updates.length <= batchChangeThreshold) {
                form.batch(() => {
                    updates.forEach(({ id, value }) => form.change(id, value));
                });
                return;
            }
            const updatesMap = Object.fromEntries(updates.map(({ id, value }) => [id, value]));
            form.reset({ ...values, ...updatesMap });
        },
        [form, values],
    );

    return (
        <React.Fragment>
            <Box className={classes.content}>
                {hasReleaseNotes ? (
                    <Box className={classes.staticSection}>
                        <Typography variant="subtitle2">{t("rolloutPanel.whatsNew")}</Typography>
                        <ReleaseNotesCard releaseNotes={releaseNotes} />
                    </Box>
                ) : null}
                <Box className={classes.devicesSection}>
                    <Typography variant="subtitle2">{t("rolloutPanel.devicesToUpdate")}</Typography>
                    <RolloutUnifiedTree
                        tree={unifiedTree}
                        values={values}
                        onChange={form.change}
                        onBatchChange={handleBatchChange}
                        codename={props.otaFile.codename}
                    />
                </Box>
            </Box>
            <Box className={classes.footer}>
                <SecondaryButton onClick={props.onCancel}>{t("common:button.cancel")}</SecondaryButton>
                <Tooltip title={t("sidepanels:rolloutPanel.button.installDisabledTooltip")} hide={!isNextDisabled}>
                    <Box component="span" className={classes.disabledButtonWrapper}>
                        <PrimaryButton onClick={props.onNext} disabled={isNextDisabled}>
                            {t("common:button.next")}
                        </PrimaryButton>
                    </Box>
                </Tooltip>
            </Box>
        </React.Fragment>
    );
};
